// PID mapping for custom SRT destinations and the push target the media sidecar builds from them.
// Rules mirror core (api/srtPids.js) and the engine: PIDs 32-8190 in decimal or 0x hex, `v` / `a`
// are the first video / audio track and `v2` / `a2` ... further ones (1-based). The engine silently
// ignores an entry whose PID is the PMT PID or already taken, so the form rejects both.

export const PID_MIN = 32
export const PID_MAX = 8190
export const DEFAULT_PMT_PID = 4096
export const PASSPHRASE_MASK = '••••'
// libsrt SRTO_PASSPHRASE
export const PASSPHRASE_MIN = 10
export const PASSPHRASE_MAX = 79
export const PASSPHRASE_ERROR = `Passphrase must be ${PASSPHRASE_MIN}–${PASSPHRASE_MAX} characters`

// Destination protocols whose push target carries a PID map. A UDP / RIST destination type opts in
// by being listed here and passing its protocol to SrtPidMapping.
export const PID_MAPPING_PROTOCOLS = ['srt']

const SOURCE = 'source'
const TS_PULL_SCHEMES = ['srt', 'udp', 'rist', 'tcp', 'tsudp', 'tsrtp', 'tstcp']

const isBlank = (value) => value === undefined || value === null || String(value).trim() === ''

export function supportsPidMapping(protocol) {
	return PID_MAPPING_PROTOCOLS.includes(String(protocol || '').toLowerCase())
}

/**
 * Whether the stream's tracks arrive in MPEG-TS, which is the only case "Keep source PIDs" differs
 * from the default: 'ts' (SRT / UDP / RIST / TCP / HTTP-TS pull), 'other' (RTMP / RTSP / HLS /
 * WebRTC pull, pre-recorded file playback) or null when it depends on what the publisher uses
 * (a live stream accepts RTMP and SRT publishes alike) or there is no stream.
 * @param {object} [stream]
 * @returns {'ts'|'other'|null}
 */
export function streamIngestKind(stream) {
	if (!stream) return null
	if (stream.type === 'vod' || stream.type === 'scheduled') return 'other'
	const pull = String(stream.pullUrl || '').trim().toLowerCase()
	if (!pull) return null
	const scheme = pull.split('://')[0]
	if (TS_PULL_SCHEMES.includes(scheme)) return 'ts'
	if (scheme === 'http' || scheme === 'https') {
		const path = pull.split('?')[0]
		if (path.endsWith('.ts') || path.includes('mpegts')) return 'ts'
		return 'other'
	}
	if (['rtmp', 'rtmps', 'rtsp', 'rtsps', 'webrtc', 'whep', 'whip'].includes(scheme)) return 'other'
	return null
}

/**
 * SRT passphrase length (libsrt 10-79). Empty means no encryption. `stored` is the saved value on
 * edit: kept unchanged it is accepted, so destinations saved before this rule can still be edited.
 * @returns {string|null}
 */
export function srtPassphraseError(value, { stored } = {}) {
	if (isBlank(value)) return null
	const s = String(value)
	if (s.length >= PASSPHRASE_MIN && s.length <= PASSPHRASE_MAX) return null
	if (!isBlank(stored) && s === String(stored)) return null
	return PASSPHRASE_ERROR
}

/**
 * @param {string|number} raw
 * @returns {{ empty?: true, pid?: number, text?: string, error?: 'format'|'range' }}
 */
export function parsePid(raw) {
	if (isBlank(raw)) return { empty: true }
	const s = String(raw).trim()
	let pid
	let hex = false
	if (/^0x[0-9a-f]+$/i.test(s)) {
		pid = parseInt(s.slice(2), 16)
		hex = true
	} else if (/^[0-9]+$/.test(s)) {
		pid = parseInt(s, 10)
	} else {
		return { error: 'format' }
	}
	if (pid < PID_MIN || pid > PID_MAX) return { pid, error: 'range' }
	return { pid, text: hex ? `0x${pid.toString(16).toUpperCase()}` : String(pid) }
}

export function pidErrorText(error) {
	return error === 'format'
		? 'Enter a number (e.g. 211) or hex (e.g. 0xD3)'
		: `PID must be between ${PID_MIN} and ${PID_MAX}`
}

/**
 * Track selector -> canonical key (`v`, `a2`, `scte35`); null when unknown.
 * @param {string} raw
 */
export function normaliseTrackKey(raw) {
	const sel = String(raw ?? '').trim().toLowerCase()
	if (sel === 'scte35') return 'scte35'
	const match = sel.match(/^(v|video|a|audio)([1-9][0-9]?)?$/)
	if (!match) return null
	const ordinal = match[2] ? Number(match[2]) : 1
	return ordinal === 1 ? match[1][0] : `${match[1][0]}${ordinal}`
}

/** `v` -> "Video PID", `a2` -> "Audio 2 PID", `scte35` -> "SCTE-35 PID" */
export function trackLabel(key) {
	if (key === 'scte35') return 'SCTE-35 PID'
	const type = key[0] === 'v' ? 'Video' : 'Audio'
	const ordinal = key.slice(1)
	return ordinal ? `${type} ${ordinal} PID` : `${type} PID`
}

/** Next unused key of a type: `v2`, then `v3` ... (`v` / `a` are the base rows). */
export function nextTrackKey(rows, type) {
	const used = new Set(rows.map((row) => row.key))
	for (let ordinal = 2; ordinal < 100; ordinal++) {
		const key = `${type}${ordinal}`
		if (!used.has(key)) return key
	}
	return null
}

const defaultRows = () => [{ key: 'v', pid: '' }, { key: 'a', pid: '' }]

/**
 * Stored `pids` -> form state. `extra` keeps entries the chosen mode has no field for (e.g. a
 * `source,v:300` map written through the API) so saving the form does not drop them.
 * @param {string} spec
 * @returns {{ mode: 'standard'|'source'|'custom', scte35: string, rows: Array<{key: string, pid: string}>, extra: string[] }}
 */
export function parsePidsSpec(spec) {
	const state = { mode: 'standard', scte35: '', rows: defaultRows(), extra: [] }
	if (isBlank(spec)) return state

	const items = String(spec).split(',').map((item) => item.trim()).filter(Boolean)
	const source = items.some((item) => item.toLowerCase() === SOURCE)
	const entries = items
		.filter((item) => item.toLowerCase() !== SOURCE)
		.map((item) => {
			const colon = item.indexOf(':')
			const key = colon > 0 ? normaliseTrackKey(item.slice(0, colon)) : null
			return { key, pid: colon > 0 ? item.slice(colon + 1).trim() : '', raw: item }
		})

	if (source) {
		state.mode = 'source'
		for (const entry of entries) {
			if (entry.key === 'scte35' && !state.scte35) state.scte35 = entry.pid
			else state.extra.push(entry.key ? `${entry.key}:${entry.pid}` : entry.raw)
		}
		return state
	}

	state.mode = 'custom'
	for (const entry of entries) {
		if (!entry.key) {
			state.extra.push(entry.raw)
			continue
		}
		const row = state.rows.find((r) => r.key === entry.key)
		if (row && !row.pid) row.pid = entry.pid
		else if (!row) state.rows.push({ key: entry.key, pid: entry.pid })
		else state.extra.push(`${entry.key}:${entry.pid}`)
	}
	return state
}

const canonicalPid = (raw) => parsePid(raw).text || String(raw).trim()

/**
 * Form state -> the `pids` value to save: '' (engine defaults), `source[,scte35:PID]`, or the
 * filled custom rows in order. Plain commas, never `%2C`.
 */
export function buildPidsSpec({ mode, scte35 = '', rows = [], extra = [] } = {}) {
	if (mode === 'source') {
		const parts = [SOURCE, ...extra]
		if (!isBlank(scte35)) parts.push(`scte35:${canonicalPid(scte35)}`)
		return parts.join(',')
	}
	if (mode === 'custom') {
		const parts = rows.filter((row) => !isBlank(row.pid)).map((row) => `${row.key}:${canonicalPid(row.pid)}`)
		return [...parts, ...extra].join(',')
	}
	return ''
}

/**
 * Inline validation for the form. Field ids: `pmtPid`, `scte35` (source mode), `rows.<index>`
 * (custom mode), `rows` (custom mode with nothing filled). A PID used twice or equal to the PMT
 * PID is an error: the engine would silently ignore the entry.
 * @returns {{ errors: Object<string, string>, valid: boolean }}
 */
export function validatePidMapping({ mode, scte35 = '', rows = [], pmtPid = '' } = {}) {
	const errors = {}

	const pmt = parsePid(pmtPid)
	if (pmt.error) errors.pmtPid = pidErrorText(pmt.error)
	const pmtValue = pmt.error || pmt.empty ? DEFAULT_PMT_PID : pmt.pid

	let entries = []
	if (mode === 'source') {
		entries = [{ field: 'scte35', key: 'scte35', pid: scte35 }]
	} else if (mode === 'custom') {
		entries = rows.map((row, index) => ({ field: `rows.${index}`, key: row.key, pid: row.pid }))
		if (!entries.some((entry) => !isBlank(entry.pid))) errors.rows = 'Enter at least one PID, or choose Standard'
	}

	const keys = new Set()
	const pids = new Map()
	for (const entry of entries) {
		const pid = parsePid(entry.pid)
		if (pid.empty) continue
		if (pid.error) {
			errors[entry.field] = pidErrorText(pid.error)
			continue
		}
		if (keys.has(entry.key)) {
			errors[entry.field] = `${trackLabel(entry.key)} is already mapped`
			continue
		}
		keys.add(entry.key)
		if (pid.pid === pmtValue) {
			errors[entry.field] = `Can't be the PMT PID (${pmtValue})`
		} else if (pids.has(pid.pid)) {
			errors[entry.field] = `Already used by ${trackLabel(pids.get(pid.pid))}`
		} else {
			pids.set(pid.pid, entry.key)
		}
	}

	return { errors, valid: Object.keys(errors).length === 0 }
}

/**
 * The URL the sidecar pushes to, e.g. `srt://host:port?streamid=…&latency=…&passphrase=…&pids=…&pmt_pid=…`.
 * The first option follows `?`, every further one `&`; empty options are left out. Connection
 * options (streamid / latency / passphrase) are SRT's; other protocols only carry the PID options.
 * @param {{ host?: string, port?: string|number, streamId?: string, latency?: string|number, passphrase?: string, pids?: string, pmtPid?: string|number }} target
 * @param {{ protocol?: string, maskPassphrase?: boolean }} [opt]
 */
export function buildPushTarget(target = {}, { protocol = 'srt', maskPassphrase = true } = {}) {
	const { host, port, streamId, latency, passphrase, pids, pmtPid } = target
	const scheme = String(protocol || 'srt').toLowerCase()
	const isSrt = scheme === 'srt'
	const portText = isBlank(port) ? '' : String(port).trim()
	let hostText = String(host ?? '').trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').replace(/\/+$/, '')
	if (portText && hostText.endsWith(`:${portText}`)) hostText = hostText.slice(0, -(portText.length + 1))

	let url = `${scheme}://${hostText || 'host'}${portText ? `:${portText}` : ''}`
	const options = [
		['streamid', isSrt ? streamId : ''],
		['latency', isSrt ? latency : ''],
		['passphrase', isSrt && !isBlank(passphrase) && maskPassphrase ? PASSPHRASE_MASK : (isSrt ? passphrase : '')],
		['pids', isBlank(pids) ? '' : String(pids).trim().replace(/%2C/gi, ',')],
		['pmt_pid', isBlank(pmtPid) ? '' : canonicalPid(pmtPid)],
	]
	for (const [name, value] of options) {
		if (isBlank(value)) continue
		url += `${url.includes('?') ? '&' : '?'}${name}=${String(value).trim()}`
	}
	return url
}

export function buildSrtPushTarget(target = {}, opt = {}) {
	return buildPushTarget(target, { ...opt, protocol: 'srt' })
}

/** Only custom SRT destinations carry a PID map (not RTMP, presets or "Stream within Castr"). */
export function isCustomSrtDestination(platform) {
	return !!platform && platform.template === 'custom' && platform.mode === 'srt' && !platform.castrStream
}

const decimalPid = (raw) => {
	const res = parsePid(raw)
	return res.text ? String(res.pid) : String(raw).trim()
}

/**
 * Card line text, all PIDs in decimal whatever was typed: "source", "v 211 · a 221 · a2 222",
 * "source · scte35 496", plus " · PMT 480" only when the PMT PID is not the default 4096.
 * null when nothing differs from the defaults.
 */
export function formatPidsSummary(pids, pmtPid) {
	const pmt = parsePid(pmtPid)
	const customPmt = pmt.text && pmt.pid !== DEFAULT_PMT_PID
	if (isBlank(pids) && !customPmt) return null
	const parts = isBlank(pids)
		? ['default']
		: String(pids).split(',').map((item) => item.trim()).filter(Boolean).map((item) => {
			const colon = item.indexOf(':')
			return colon < 0 ? item : `${item.slice(0, colon)} ${decimalPid(item.slice(colon + 1))}`
		})
	if (customPmt) parts.push(`PMT ${pmt.pid}`)
	return parts.join(' · ')
}
